import React from "react";
import { FiSearch } from "react-icons/fi";
import type { Chat } from "../../../../redux/reducers/ChatReducer/ChatSliceTypes";
import type { User } from "../../../../redux/reducers/UserSlice/UserSliceType";
import SearchBar from "./SearchBar";
import FilterTabs from "./Filtertabs"; // Fix the case here too!
import ChatListItem from "./ChatListItem";
import { UI_TEXT } from "../../constants/messages.constants";
interface ChatSidebarProps {
  currentUser: User | null;
  chats: Chat[];
  selectedChat: Chat | null;
  filter: "all" | "unread";
  searchQuery: string;
  showSearch: boolean;
  loading: boolean;
  isLoadingMore: boolean;
  searchInputRef: React.RefObject<HTMLInputElement>;
  onChatSelect: (chat: Chat) => void;
  onFilterChange: (filter: "all" | "unread") => void;
  onSearchChange: (query: string) => void;
  onClearSearch: () => void;
  onToggleSearch: () => void;
  onLoadMore: () => void;
  isMobile?: boolean;
}

/**
 * ChatSidebar Component
 * Left panel containing search, filters, and chat list
 */
const ChatSidebar: React.FC<ChatSidebarProps> = ({
  currentUser,
  chats,
  selectedChat,
  filter,
  searchQuery,
  showSearch,
  loading,
  isLoadingMore,
  searchInputRef,
  onChatSelect,
  onFilterChange,
  onSearchChange,
  onClearSearch,
  onToggleSearch,
  onLoadMore,
  isMobile = false,
}) => {
  // Infinite scroll handler
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const bottom =
      target.scrollHeight - target.scrollTop === target.clientHeight;
    if (bottom && !isLoadingMore) {
      onLoadMore();
    }
  };

  return (
    <div
      className={`${
        isMobile ? "w-full" : "w-full md:w-1/3 lg:w-1/4"
      } border-r border-neutral-200 bg-white dark:bg-neutral-800 dark:border-neutral-700 h-full flex flex-col`}
    >
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 sm:py-5 flex-shrink-0 border-b border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white">
            {UI_TEXT.SECTIONS.MESSAGES}
          </h2>
          <button
            onClick={onToggleSearch}
            className={`p-2.5 text-neutral-600 hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-white rounded-xl transition-all ${
              showSearch
                ? "bg-neutral-100 dark:bg-neutral-700"
                : "hover:bg-neutral-100 dark:hover:bg-neutral-700"
            }`}
            aria-label="Toggle search"
          >
            <FiSearch size={20} />
          </button>
        </div>

        {/* Search Bar */}
        {showSearch && (
          <SearchBar
            value={searchQuery}
            onChange={onSearchChange}
            onClear={onClearSearch}
            placeholder={UI_TEXT.PLACEHOLDERS.SEARCH}
            autoFocus={true}
          />
        )}

        {/* Filter Buttons */}
        <FilterTabs activeFilter={filter} onFilterChange={onFilterChange} />
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto" onScroll={handleScroll}>
        {loading && chats.length === 0 ? (
          <div className="flex justify-center items-center py-12">
            <div className="w-10 h-10 border-4 border-neutral-300 border-t-[#F56948] rounded-full animate-spin"></div>
          </div>
        ) : chats.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="w-16 h-16 mb-4 rounded-full bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center">
              <FiSearch size={28} className="text-neutral-400" />
            </div>
            <p className="text-neutral-600 dark:text-neutral-400 font-medium">
              {searchQuery ? "No conversations found" : "No conversations yet"}
            </p>
            <p className="text-sm text-neutral-500 dark:text-neutral-500 mt-1">
              {searchQuery
                ? "Try adjusting your search"
                : "Start a new conversation"}
            </p>
          </div>
        ) : (
          <>
            {chats.map((chat) => (
              <ChatListItem
                key={chat.id}
                chat={chat}
                isSelected={selectedChat?.id === chat.id}
                currentUser={currentUser}
                onClick={onChatSelect}
              />
            ))}
            {isLoadingMore && (
              <div className="flex justify-center py-4">
                <div className="w-6 h-6 border-2 border-neutral-300 border-t-[#F56948] rounded-full animate-spin"></div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ChatSidebar;
